import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, Spin, Popconfirm } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  sendChatMessage, sendChatMessageWithConfig,
  getConversations, getConversation, deleteConversation,
  getPerformanceCycles, getPlansForAutocomplete,
} from "../api/chatbot/ChatbotApi";
import ChartRenderer from "./Chatbot/ChartRenderer";
import { ChartParser } from "../utils/ChartParser";
import { getMarkdownComponents } from "../utils/markdownComponents";
import SafeMarkdown from "./SafeMarkdown";
import stratLogo from "../assets/images/logo.png";
import { buildAdvisorContext } from "../utils/buildAdvisorContext";
import { detectPlanQuery, resolveNumberedPlanReferences } from "../utils/detectPlanQuery";
import SearchTopLevelPlansApi from "../api/plans/SearchTopLevelPlansApi";

// ── Design tokens ─────────────────────────────────────────────────────────────
const DARK       = "#0d1b2a";
const DARKER     = "#091525";
const SIDEBAR_BG = "#0a1520";
const CARD_BG    = "#112236";
const ACCENT     = "#F15A24";
const BORDER     = "rgba(241,90,36,0.3)";
const TEXT_DIM   = "#64748b";
const TEXT_MID   = "#94a3b8";

// ── GEM icon — uses the Strategizer logo ─────────────────────────────────────
function GemIcon({ size = 28, style = {} }) {
  return (
    <div style={{
      width: size, height: size,
      borderRadius: Math.round(size * 0.32) + "px",
      background: `linear-gradient(135deg, ${ACCENT}, #c0390f)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, overflow: "hidden", ...style,
    }}>
      <img
        src={stratLogo}
        alt="Strategizer"
        style={{ width: size * 0.65, height: size * 0.65, objectFit: "contain", filter: "brightness(0) invert(1)" }}
      />
    </div>
  );
}

// ── Sidebar conversation item ──────────────────────────────────────────────────
function ConvItem({ conv, active, onSelect, onDelete }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={() => onSelect(conv.conversationId)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 10px",
        borderRadius: "8px",
        cursor: "pointer",
        background: active
          ? "rgba(241,90,36,0.12)"
          : hovered ? "rgba(255,255,255,0.04)" : "transparent",
        borderLeft: active ? `3px solid ${ACCENT}` : "3px solid transparent",
        transition: "all 0.15s",
        marginBottom: "2px",
      }}
    >
      {/* Checkbox-style indicator */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", flex: 1, minWidth: 0 }}>
        <div style={{
          width: "14px", height: "14px", borderRadius: "3px",
          border: `1.5px solid ${active ? ACCENT : "rgba(255,255,255,0.2)"}`,
          background: active ? `rgba(241,90,36,0.15)` : "transparent",
          flexShrink: 0, marginTop: "2px",
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: "12px", fontWeight: active ? 600 : 400,
            color: active ? "#fff" : TEXT_MID,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {conv.title || "New Conversation"}
          </div>
          <div style={{ fontSize: "10px", color: TEXT_DIM, marginTop: "1px" }}>
            {new Date(conv.updatedAt).toLocaleDateString()}
          </div>
        </div>
      </div>
      {(hovered || active) && (
        <Popconfirm
          title="Delete this conversation?"
          onConfirm={e => { e.stopPropagation(); onDelete(conv.conversationId); }}
          okText="Delete" cancelText="Cancel" placement="right"
        >
          <Button
            type="text" icon={<DeleteOutlined />} size="small"
            onClick={e => e.stopPropagation()}
            style={{ color: TEXT_DIM, flexShrink: 0, marginLeft: "4px" }}
          />
        </Popconfirm>
      )}
    </div>
  );
}

// ── Suggestion chip ────────────────────────────────────────────────────────────
function SuggestionChip({ title, subtitle, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: 1, minWidth: "160px", maxWidth: "260px",
        background: hovered ? "rgba(241,90,36,0.08)" : CARD_BG,
        border: `1px solid ${hovered ? BORDER : "rgba(255,255,255,0.07)"}`,
        borderRadius: "12px",
        padding: "14px 16px",
        textAlign: "left",
        cursor: "pointer",
        transition: "all 0.15s",
        display: "flex", alignItems: "center", gap: "10px",
      }}
    >
      <div style={{
        width: "28px", height: "28px", borderRadius: "8px",
        background: "rgba(241,90,36,0.12)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M9 18l6-6-6-6" stroke={ACCENT} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "12px", fontWeight: 600, color: "#fff", lineHeight: 1.4 }}>{title}</div>
        {subtitle && <div style={{ fontSize: "10px", color: TEXT_DIM, marginTop: "2px", lineHeight: 1.3 }}>{subtitle}</div>}
      </div>
    </button>
  );
}

// ── Message bubble ─────────────────────────────────────────────────────────────
function MessageBubble({ msg, userImage, onSuggestionClick }) {
  const isUser = msg.role === "user";

  if (isUser) {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "18px", gap: "10px", alignItems: "flex-end" }}>
        <div style={{
          maxWidth: "72%",
          background: ACCENT,
          color: "#fff",
          padding: "12px 18px",
          borderRadius: "18px 18px 4px 18px",
          fontSize: "14px",
          lineHeight: "1.65",
        }}>
          {msg.content}
          <div style={{ fontSize: "10px", opacity: 0.65, marginTop: "6px", textAlign: "right" }}>
            {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
        {/* User avatar */}
        <div style={{
          width: "32px", height: "32px", borderRadius: "50%",
          background: userImage ? "transparent" : "rgba(241,90,36,0.2)",
          border: `2px solid ${ACCENT}`,
          flexShrink: 0, overflow: "hidden",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {userImage
            ? <img src={userImage} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke={ACCENT} strokeWidth="2"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke={ACCENT} strokeWidth="2" strokeLinecap="round"/></svg>
          }
        </div>
      </div>
    );
  }

  // Assistant
  const parsed = ChartParser.parse(msg.content);
  const textContent = parsed.text || msg.content;

  return (
    <div style={{ display: "flex", gap: "10px", marginBottom: "20px", alignItems: "flex-start" }}>
      <GemIcon size={30} style={{ marginTop: "2px" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          background: CARD_BG,
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: "4px 14px 14px 14px",
          padding: "14px 18px",
          fontSize: "13.5px",
          lineHeight: "1.7",
        }}>
          {msg.isLoading ? (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "12px", color: ACCENT, fontFamily: "monospace" }}>
                Analyzing dashboard KPIs...
              </span>
              <Spin size="small" />
            </div>
          ) : (
            <>
              <SafeMarkdown components={getMarkdownComponents({ primary: ACCENT, text: "#e2e8f0", headings: "#fff" })}>
                {textContent}
              </SafeMarkdown>
              {parsed.hasChart && (
                <div style={{ marginTop: "16px", borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "16px" }}>
                  <ChartRenderer type={parsed.type} data={parsed.data} config={{ width: 640, height: 340 }} />
                </div>
              )}
            </>
          )}
        </div>
        {!msg.isLoading && (
          <div style={{ fontSize: "10px", color: TEXT_DIM, marginTop: "5px", paddingLeft: "4px" }}>
            {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            {msg.model && <span style={{ marginLeft: "5px" }}>· {msg.model}</span>}
          </div>
        )}
        {/* Smart follow-up suggestion chips */}
        {!msg.isLoading && msg.suggestions?.length > 0 && (
          <div style={{ display: "flex", gap: "8px", marginTop: "10px", paddingLeft: "4px", flexWrap: "wrap" }}>
            {msg.suggestions.map((suggestion, i) => (
              <button
                key={i}
                onClick={() => onSuggestionClick?.(suggestion)}
                style={{
                  background: "rgba(241,90,36,0.06)",
                  border: "1px solid rgba(241,90,36,0.25)",
                  borderRadius: "20px",
                  padding: "6px 14px",
                  color: "#e2e8f0",
                  fontSize: "11.5px",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  lineHeight: "1.4",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = "rgba(241,90,36,0.12)";
                  e.currentTarget.style.borderColor = "rgba(241,90,36,0.5)";
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "rgba(241,90,36,0.06)";
                  e.currentTarget.style.borderColor = "rgba(241,90,36,0.25)";
                  e.currentTarget.style.color = "#e2e8f0";
                }}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── GEM Input — matches the dark pill design exactly ──────────────────────────
function GemInput({ value, onChange, onSend, loading, placeholder, configId }) {
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [cycles, setCycles] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showCycles, setShowCycles] = useState(false);
  const [showPlans, setShowPlans] = useState(false);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
    if (e.key === "Escape") { setShowCycles(false); setShowPlans(false); }
  };

  const handleChange = async (e) => {
    const v = e.target.value;
    onChange(v);
    const cursor = e.target.selectionStart || 0;
    const before = v.substring(0, cursor);
    const atMatch = before.match(/@([^@#\s]*)$/);
    const hashMatch = before.match(/#([^@#\s]*)$/);
    if (atMatch) {
      setShowPlans(false);
      try { const data = await getPerformanceCycles(atMatch[1]); setCycles(data || []); setShowCycles(true); } catch { setShowCycles(false); }
    } else if (hashMatch) {
      setShowCycles(false);
      try { const data = await getPlansForAutocomplete('', hashMatch[1]); setPlans(data || []); setShowPlans(true); } catch { setShowPlans(false); }
    } else {
      setShowCycles(false); setShowPlans(false);
    }
  };

  const selectCycle = (cycle) => {
    const cur = inputRef.current?.selectionStart || 0;
    const before = value.substring(0, cur).replace(/@([^@#\s]*)$/, `@${cycle.name} `);
    onChange(before + value.substring(cur));
    setShowCycles(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const selectPlan = (plan) => {
    const cur = inputRef.current?.selectionStart || 0;
    const before = value.substring(0, cur).replace(/#([^@#\s]*)$/, `#${plan.id}:${plan.title} `);
    onChange(before + value.substring(cur));
    setShowPlans(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      {/* Autocomplete dropdowns */}
      {showCycles && cycles.length > 0 && (
        <div style={{
          position: "absolute", bottom: "60px", left: 0, right: 0,
          background: "#0d1b2a", border: "1px solid rgba(241,90,36,0.3)",
          borderRadius: "12px", overflow: "hidden", zIndex: 100, maxHeight: "220px", overflowY: "auto",
        }}>
          {cycles.map(c => (
            <div key={c.id} onClick={() => selectCycle(c)} style={{
              padding: "10px 16px", cursor: "pointer", color: "#e2e8f0", fontSize: "13px",
              borderBottom: "1px solid rgba(255,255,255,0.04)",
            }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(241,90,36,0.1)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span style={{ color: ACCENT, marginRight: "6px" }}>@</span>{c.name}
            </div>
          ))}
        </div>
      )}
      {showPlans && plans.length > 0 && (
        <div style={{
          position: "absolute", bottom: "60px", left: 0, right: 0,
          background: "#0d1b2a", border: "1px solid rgba(241,90,36,0.3)",
          borderRadius: "12px", overflow: "hidden", zIndex: 100, maxHeight: "220px", overflowY: "auto",
        }}>
          {plans.map(p => (
            <div key={p.id} onClick={() => selectPlan(p)} style={{
              padding: "10px 16px", cursor: "pointer", color: "#e2e8f0", fontSize: "13px",
              borderBottom: "1px solid rgba(255,255,255,0.04)",
            }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(241,90,36,0.1)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span style={{ color: ACCENT, marginRight: "6px" }}>#</span>
              <span>{p.title.length > 50 ? p.title.substring(0, 50) + "…" : p.title}</span>
              <span style={{ fontSize: "11px", color: "#64748b", marginLeft: "8px" }}>{p.assignedTo}</span>
            </div>
          ))}
        </div>
      )}

      {/* The actual pill input */}
      <div style={{
        display: "flex", alignItems: "center",
        background: "rgba(255,255,255,0.04)",
        borderRadius: "50px",
        padding: "6px 6px 6px 22px",
        gap: "10px",
      }}>
        <input
          ref={inputRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKey}
          disabled={loading}
          placeholder={placeholder}
          style={{
            flex: 1,
            background: "transparent",
            border: "none",
            outline: "none",
            color: "#e2e8f0",
            fontSize: "14px",
            lineHeight: "1.5",
            caretColor: ACCENT,
          }}
        />
        <style>{`input::placeholder { color: #475569; }`}</style>
        {/* Send button */}
        <button
          onClick={onSend}
          disabled={!value.trim() || loading}
          style={{
            width: "38px", height: "38px", borderRadius: "50%",
            background: value.trim() ? "#1e3a5f" : "#1a2e45",
            border: "none",
            cursor: value.trim() ? "pointer" : "default",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, transition: "background 0.15s",
          }}
          onMouseEnter={e => { if (value.trim()) e.currentTarget.style.background = "#243f6a"; }}
          onMouseLeave={e => e.currentTarget.style.background = value.trim() ? "#1e3a5f" : "#1a2e45"}
        >
          {loading
            ? <Spin size="small" />
            : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <line x1="22" y1="2" x2="11" y2="13" stroke="#94a3b8" strokeWidth="2.2" strokeLinecap="round" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" fill="#94a3b8" />
              </svg>
            )
          }
        </button>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
function ChatInterfaceWithHistory({
  configId = null,
  conversationId = null,
  onConversationChange = null,
  botType = "GEM",
  onNewChat = null,
  initialMessageData = null,
  onInitialMessageSent = null,
  chatbotConfig = null,
  highlightNewConversation = false,
}) {
  const userState = useSelector(s => s.userState.value);
  const positionState = useSelector(s => s.positionState.value);
  const perfCycleState = useSelector(s => s.perfCycleState.value);

  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(conversationId);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false); // collapsed by default
  const [lastResolvedPlan, setLastResolvedPlan] = useState(null); // { id, title } — tracks last identified plan for follow-ups
  const [lastShownPlanList, setLastShownPlanList] = useState(null); // tracks last numbered list of plans shown to user
  const messagesEndRef = useRef(null);
  const lastLoadedRef = useRef(null);

  const botName = botType === "GEM" ? "GEM" : "CLEO";
  const botSubtitle = botType === "GEM" ? "Goal Execution Manager" : "Comprehensive Leadership Execution Orchestrator";
  const userName = `${userState?.firstName || ""} ${userState?.lastName || ""}`.trim() || "User";
  const userRole = positionState?.designations?.name || positionState?.userRole?.name || "Executive";
  const userImage = userState?.imageUrl || null;

  const SUGGESTIONS_GEM = [
    { title: "How is the performance cycle progressing?", subtitle: "Cycle health, progress vs timeline, tone analysis" },
    { title: "Which strategic objectives need attention?", subtitle: "Bottleneck plans and owner-specific actions" },
    { title: "Who is performing best and worst this cycle?", subtitle: "Employee performance tier breakdown" },
    { title: "Draft a Board briefing on overall progress", subtitle: "Executive-ready briefing memo" },
  ];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (conversationId && conversationId !== lastLoadedRef.current) loadConversation(conversationId);
  }, [conversationId]);

  useEffect(() => {
    if (configId) loadConversations();
  }, [configId]);

  useEffect(() => {
    if (initialMessageData?.message && !messages.length) {
      sendMessage(initialMessageData.message, initialMessageData.conversationId);
      onInitialMessageSent?.();
    }
  }, [initialMessageData, messages.length]);

  const loadConversations = async () => {
    setLoadingConversations(true);
    try {
      const res = await getConversations(configId, "GPT");
      const filtered = res.conversations.filter(c => !configId || c.configId === configId);
      setConversations(filtered);
      if (!conversationId && filtered.length > 0 && !currentConversationId) {
        const id = filtered[0].conversationId;
        lastLoadedRef.current = id;
        loadConversation(id);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversation = async (convId) => {
    try {
      setCurrentConversationId(convId);
      lastLoadedRef.current = convId;
      const res = await getConversation(convId);
      setMessages(res.messages || []);
    } catch {
      setCurrentConversationId(convId);
      setMessages([]);
    }
  };

  const handleNewConversation = () => {
    if (onNewChat) { onNewChat(); return; }
    const tempId = `temp_conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setConversations(prev => [{
      conversationId: tempId, title: "New Conversation",
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      configId, chatMode: "GPT", isTemporary: true,
    }, ...prev]);
    setCurrentConversationId(tempId);
    setMessages([]);
    onConversationChange?.(tempId);
  };

  // Detect what KIND of follow-up the user is asking about
  const getFollowUpType = (text) => {
    const lower = text.toLowerCase();
    if (/cascade|child plan|sub plan|cascade plan|breakdown|children/.test(lower)) return 'cascade';
    if (/split plan|split plans|splits\b/.test(lower)) return 'split';
    if (/\btasks?\b|task progress|task status/.test(lower)) return 'tasks';
    if (/\bkpis?\b|key performance/.test(lower)) return 'kpis';
    return 'general';
  };

  const PLAN_FOLLOWUP_KEYWORDS = [
    'cascade', 'child plan', 'sub plan', 'split plan', 'split plans',
    'cascade plan', 'cascade plans', 'breakdown', 'children',
    'tasks', 'task progress', 'task status',
    'kpi', 'kpis', 'key performance',
    'this plan', 'same plan', 'tell me more', 'more details',
    'what about', 'and what', 'also', 'additionally',
    'who owns', 'owner', 'owners',
  ];

  const isFollowUpAboutPlan = (text) => {
    if (!lastResolvedPlan) return false;
    const lower = text.toLowerCase();
    return PLAN_FOLLOWUP_KEYWORDS.some(k => lower.includes(k));
  };

  const ADVISOR_KEYWORDS = [
    'cycle', 'progressing', 'progress', 'performance', 'on track', 'off track',
    'behind', 'ahead', 'bottleneck', 'attention', 'board brief', 'briefing',
    'best performing', 'worst performing', 'mid performing', 'struggling',
    'who is', 'who are', 'top performer', 'lowest', 'highest', 'employee',
    'team member', 'swot', 'analysis', 'status', 'summary', 'how are we',
    'how is', 'overview', 'update', 'review',
    'what plans', 'my plans', 'all plans', 'list plans', 'show plans', 'which plans',
  ];

  const needsAdvisorContext = (text) => {
    const lower = text.toLowerCase();
    return ADVISOR_KEYWORDS.some(k => lower.includes(k));
  };

  // Helper: extract numbered plan list from bot responses
  // Detects patterns like: 1. "Plan Title" or 1. **Plan Title** or numbered lines with plan-like titles
  const extractAndSavePlanListFromResponse = async (reply, cycleId) => {
    if (!reply || !cycleId) return;
    // Match patterns: 1. "Title" | 1. **Title** | 1. Title (with bullet formatting)
    const patterns = [
      /^\s*\d+\.\s*\*\*"?([^"*\n]+)"?\*\*/gm,  // 1. **"Plan Title"** or 1. **Plan Title**
      /^\s*\d+\.\s*"([^"\n]+)"/gm,               // 1. "Plan Title"
    ];

    let planTitles = [];
    for (const pattern of patterns) {
      let match;
      while ((match = pattern.exec(reply)) !== null) {
        const title = match[1].trim();
        if (title.length >= 6) planTitles.push(title);
      }
      if (planTitles.length >= 2) break; // found a good list
    }

    if (planTitles.length < 2) return; // Not a plan list

    // Resolve plan titles to objects with IDs
    try {
      const plansWithIds = [];
      for (const title of planTitles.slice(0, 10)) { // max 10
        const searchResult = await SearchTopLevelPlansApi(title, cycleId);
        if (searchResult?.success && searchResult.data?.length > 0) {
          const match = searchResult.data.find(p => p.title.toLowerCase() === title.toLowerCase()) || searchResult.data[0];
          plansWithIds.push(match);
        }
      }
      if (plansWithIds.length >= 2) {
        setLastShownPlanList(plansWithIds);
      }
    } catch {
      // silently fail — this is best-effort tracking
    }
  };

  const sendMessage = async (text, convId) => {
    const userMsg = { role: "user", content: text, timestamp: new Date().toISOString() };
    const loadingMsg = { role: "assistant", content: "", isLoading: true, timestamp: new Date().toISOString() };
    setMessages(prev => [...(prev || []), userMsg, loadingMsg]);
    setLoading(true);
    try {
      const isTemp = convId?.startsWith("temp_conv_");
      const idToSend = isTemp ? null : convId;

      // ── Step 0: Follow-up about last resolved plan ──
      if (isFollowUpAboutPlan(text) && lastResolvedPlan) {
        const followUpType = getFollowUpType(text);

        // Build a directive that tells GPT exactly what to produce
        const directives = {
          cascade: `[INSTRUCTION: The user is asking for a CASCADE PLAN DEEP-DIVE. Use the PLAN FOLLOW-UP INTELLIGENCE cascade section. DO NOT repeat the parent plan status report. List EACH cascade plan individually with performance, target, owner, and one specific action per underperforming plan.]`,
          split: `[INSTRUCTION: The user is asking for a SPLIT PLAN DEEP-DIVE. Use the PLAN FOLLOW-UP INTELLIGENCE split section. DO NOT repeat the parent plan status report. Analyse EACH split plan individually — title, owner, performance%, target with unit, achieved vs target.]`,
          tasks: `[INSTRUCTION: The user is asking about TASKS. List all tasks from the tasks data — name, status, assigned to, dates. Highlight overdue incomplete tasks. Count completed vs total.]`,
          kpis: `[INSTRUCTION: The user is asking about KPIs. List all KPIs from the kpis data — name, target, achieved, performance%. Flag any KPI below 50% as at risk.]`,
          general: `[INSTRUCTION: The user is asking a follow-up about the last plan. Answer from the plan data in context — do not repeat the full status report.]`,
        };

        const directive = directives[followUpType] || directives.general;
        const enrichedText = `#${lastResolvedPlan.id}:${lastResolvedPlan.title}\n\n${directive}\n\nUser question: ${text}`;

        const res = configId
          ? await sendChatMessageWithConfig(enrichedText, idToSend, configId, "GPT")
          : await sendChatMessage(enrichedText, idToSend, "GPT");

        if (isTemp || !convId || convId !== res.conversationId) {
          setCurrentConversationId(res.conversationId);
          onConversationChange?.(res.conversationId);
          if (isTemp) setConversations(prev => prev.filter(c => c.conversationId !== convId));
          loadConversations();
        }

        setMessages(prev => [
          ...prev.slice(0, -1),
          { role: "assistant", content: res.reply, timestamp: new Date().toISOString(), model: res.model, suggestions: res.suggestions },
        ]);
        setLoading(false);
        return;
      }

      // ── Step 1: Check if user is selecting from a numbered list ──
      if (lastShownPlanList && lastShownPlanList.length > 0) {
        const numberedResult = resolveNumberedPlanReferences(text, lastShownPlanList);

        if (numberedResult.type === 'numbered_single') {
          const plan = numberedResult.plans[0];
          // Save for follow-up conversations
          setLastResolvedPlan({ id: plan.id, title: plan.title });
          setLastShownPlanList(null); // clear the list now that user picked
          const enrichedText = `#${plan.id}:${plan.title}\n\n${text}`;
          const res = configId
            ? await sendChatMessageWithConfig(enrichedText, idToSend, configId, "GPT")
            : await sendChatMessage(enrichedText, idToSend, "GPT");

          if (isTemp || !convId || convId !== res.conversationId) {
            setCurrentConversationId(res.conversationId);
            onConversationChange?.(res.conversationId);
            if (isTemp) setConversations(prev => prev.filter(c => c.conversationId !== convId));
            loadConversations();
          }

          setMessages(prev => [
            ...prev.slice(0, -1),
            { role: "assistant", content: res.reply, timestamp: new Date().toISOString(), model: res.model, suggestions: res.suggestions },
          ]);
          setLoading(false);
          return;
        }

        if (numberedResult.type === 'numbered_multi') {
          // Multiple plans selected — fetch analysis for each
          const selectedPlans = numberedResult.plans;
          setLastShownPlanList(null); // clear the list
          // Build enriched text with all selected plan IDs
          const planRefs = selectedPlans.map(p => `#${p.id}:${p.title}`).join('\n');
          const enrichedText = `${planRefs}\n\n[INSTRUCTION: The user selected ${selectedPlans.length} plans from the list. Give a brief summary of each plan's status — title, performance%, owner, target, and any key issues. Keep it concise per plan.]\n\nUser question: ${text}`;
          const res = configId
            ? await sendChatMessageWithConfig(enrichedText, idToSend, configId, "GPT")
            : await sendChatMessage(enrichedText, idToSend, "GPT");

          if (isTemp || !convId || convId !== res.conversationId) {
            setCurrentConversationId(res.conversationId);
            onConversationChange?.(res.conversationId);
            if (isTemp) setConversations(prev => prev.filter(c => c.conversationId !== convId));
            loadConversations();
          }

          // Save first plan as last resolved for follow-ups
          setLastResolvedPlan({ id: selectedPlans[0].id, title: selectedPlans[0].title });

          setMessages(prev => [
            ...prev.slice(0, -1),
            { role: "assistant", content: res.reply, timestamp: new Date().toISOString(), model: res.model, suggestions: res.suggestions },
          ]);
          setLoading(false);
          return;
        }
      }

      // ── Step 2: Check if user is asking about a specific plan by name ──
      if (perfCycleState?.id) {
        const planQuery = await detectPlanQuery(text, perfCycleState.id, perfCycleState.name);

        if (planQuery.type === 'not_found') {
          // Replace loading bubble with not-found message — no GPT call needed
          setMessages(prev => [
            ...prev.slice(0, -1),
            { role: "assistant", content: planQuery.notFoundMessage, timestamp: new Date().toISOString() },
          ]);
          setLoading(false);
          return;
        }

        if (planQuery.type === 'ambiguous') {
          // Save the plan list so user can reference by number later
          setLastShownPlanList(planQuery.plans);
          // Ask for clarification — no GPT call
          setMessages(prev => [
            ...prev.slice(0, -1),
            { role: "assistant", content: planQuery.clarificationMessage, timestamp: new Date().toISOString() },
          ]);
          setLoading(false);
          return;
        }

        if (planQuery.type === 'single') {
          const plan = planQuery.plan;
          // Save for follow-up conversations
          setLastResolvedPlan({ id: plan.id, title: plan.title });
          // Use exact #planId:planTitle format — chatbot-context.service.ts
          // detects this in enhanceMessageWithContext and calls getPlanAnalysisData()
          // which fetches cascade plans, split plans, progress history, employee analysis
          let planTypeHint = '';
          if (plan.planType && plan.planType !== 'top-level') {
            planTypeHint = `\n[PLAN CONTEXT: This is a ${plan.planType} plan${plan.planLevel ? ` at the "${plan.planLevel}" level` : ''}${plan.parentPlanTitle ? `, cascaded/split from parent plan "${plan.parentPlanTitle}"` : ''}. Analyse it in that context — show its relationship to the parent plan.]`;
          } else if (plan.planLevel) {
            planTypeHint = `\n[PLAN CONTEXT: This plan is classified as a "${plan.planLevel}" in the organization's template hierarchy.]`;
          }
          const enrichedText = `#${plan.id}:${plan.title}${planTypeHint}\n\n${text}`;
          const res = configId
            ? await sendChatMessageWithConfig(enrichedText, idToSend, configId, "GPT")
            : await sendChatMessage(enrichedText, idToSend, "GPT");

          if (isTemp || !convId || convId !== res.conversationId) {
            setCurrentConversationId(res.conversationId);
            onConversationChange?.(res.conversationId);
            if (isTemp) setConversations(prev => prev.filter(c => c.conversationId !== convId));
            loadConversations();
          }

          setMessages(prev => [
            ...prev.slice(0, -1),
            { role: "assistant", content: res.reply, timestamp: new Date().toISOString(), model: res.model, suggestions: res.suggestions },
          ]);
          setLoading(false);
          return;
        }

        // ── Fallback: If no pattern matched but the message looks like it could be a plan name,
        // try a direct search. This catches cases like "Grow Rockland local product sale by 50%"
        // said without any "tell me about" or "status of" wrapper.
        // NOTE: we intentionally do NOT gate this on !needsAdvisorContext.
        // Words like "analysis", "status", "overview" trigger advisor context AND
        // are exactly how users ask about a specific plan ("<Plan Name> analysis").
        // Previously that collision skipped plan resolution and fell through to the
        // cycle-wide advisor context, so a single-plan question got answered with
        // cycle data (wrong 0%). Try to resolve a specific plan first; if the
        // search finds nothing we still fall through to advisor context below.
        // Cycle/aggregate-level questions ("how are we progressing", "worst
        // performing plans", "who needs attention") should NOT be treated as a
        // single-plan lookup — let them fall through to advisor cycle context.
        const isCycleLevelQuery = /\b(how\s+are\s+we|how'?s\s+(?:the\s+)?(?:cycle|performance|it\s+going)|cycle|overall|organisation|organization|all\s+plans|my\s+plans|which\s+plans|what\s+plans|list\s+plans|show\s+plans|worst\s+performing|best\s+performing|top\s+performer|who\s+(?:is|are|needs)|board\s+brief|briefing|employee|team\s+member|attention\s+required|needs\s+attention)\b/i.test(text);

        if (planQuery.type === 'no_match' && text.trim().length >= 10 && !/#\d+\s*:/.test(text) && !isCycleLevelQuery) {
          // Strip filler AND trailing intent words so a message like
          // "Achieve Total Revenue of 20M analysis" searches for the title
          // "Achieve Total Revenue of 20M" (previously the trailing "analysis"
          // stayed in the query and never matched a plan title).
          const directQuery = text.trim()
            // leading request phrases
            .replace(/^(tell\s+me\s+about|what\s+about|show\s+me|give\s+me|how\s+is|how'?s|progress\s+on|status\s+of|overview\s+of|update\s+on|analysis\s+(?:on|of|for)|analyse|analyze)\s+/gi, '')
            // trailing request/filler words (one or more, in any order)
            .replace(/(\s+(analysis|analyse|analyze|overview|update|progress|status|review|report|details?|info|summary|please|now|tell\s+me|about\s+(?:this|it)|for\s+me))+\s*[?.!]*$/gi, '')
            .trim();

          if (directQuery.length >= 6) {
            const directResult = await SearchTopLevelPlansApi(directQuery, perfCycleState.id);
            if (directResult?.success && directResult.data?.length > 0) {
              if (directResult.data.length === 1) {
                const plan = directResult.data[0];
                setLastResolvedPlan({ id: plan.id, title: plan.title });
                let planTypeHint = '';
                if (plan.planType && plan.planType !== 'top-level') {
                  planTypeHint = `\n[PLAN CONTEXT: This is a ${plan.planType} plan${plan.planLevel ? ` at the "${plan.planLevel}" level` : ''}${plan.parentPlanTitle ? `, cascaded/split from parent plan "${plan.parentPlanTitle}"` : ''}. Analyse it in that context.]`;
                }
                const enrichedText = `#${plan.id}:${plan.title}${planTypeHint}\n\n${text}`;
                const res = configId
                  ? await sendChatMessageWithConfig(enrichedText, idToSend, configId, "GPT")
                  : await sendChatMessage(enrichedText, idToSend, "GPT");

                if (isTemp || !convId || convId !== res.conversationId) {
                  setCurrentConversationId(res.conversationId);
                  onConversationChange?.(res.conversationId);
                  if (isTemp) setConversations(prev => prev.filter(c => c.conversationId !== convId));
                  loadConversations();
                }

                setMessages(prev => [
                  ...prev.slice(0, -1),
                  { role: "assistant", content: res.reply, timestamp: new Date().toISOString(), model: res.model, suggestions: res.suggestions },
                ]);
                setLoading(false);
                return;
              } else if (directResult.data.length > 1) {
                // Multiple matches — ask user to pick
                setLastShownPlanList(directResult.data);
                const listText = directResult.data
                  .map((p, i) => `${i + 1}. **${p.title}**${p.planLevel ? ` _(${p.planLevel})_` : ''}`)
                  .join('\n');
                setMessages(prev => [
                  ...prev.slice(0, -1),
                  { role: "assistant", content: `I found ${directResult.data.length} plans that might match:\n\n${listText}\n\nWhich one would you like to know about? Reply with the number.`, timestamp: new Date().toISOString() },
                ]);
                setLoading(false);
                return;
              }
            }
          }
        }
      }

      // ── Step 3: Normal flow — cycle/employee context injection ──
      let messageToSend = text;
      if (needsAdvisorContext(text) && positionState?.id && perfCycleState?.id) {
        const ctx = await buildAdvisorContext({
          positionId: positionState.id,
          perfCycleId: perfCycleState.id,
          cycleName: perfCycleState.name,
          cycleStart: perfCycleState.startingOn,
          cycleEnd: perfCycleState.endingOn,
        });
        if (ctx) {
          messageToSend = ctx + '\n\n' + text;
          // Extract plan names from the context for numbered reference tracking
          // The advisor context shows plans in numbered format like: 1. "Plan Title" | Owner...
          const planLines = ctx.match(/\d+\.\s*"([^"]+)"/g);
          if (planLines && planLines.length > 0) {
            const extractedPlans = planLines.map(line => {
              const titleMatch = line.match(/"([^"]+)"/);
              return titleMatch ? { title: titleMatch[1], id: null } : null;
            }).filter(Boolean);
            if (extractedPlans.length > 0) {
              // We'll set the plan list after we get the response (since these don't have IDs yet)
              // Search for them to get IDs
              const plansWithIds = [];
              for (const ep of extractedPlans) {
                const searchResult = await SearchTopLevelPlansApi(ep.title, perfCycleState.id);
                if (searchResult?.success && searchResult.data?.length > 0) {
                  // Find exact or closest match
                  const match = searchResult.data.find(p => p.title.toLowerCase() === ep.title.toLowerCase()) || searchResult.data[0];
                  plansWithIds.push(match);
                }
              }
              if (plansWithIds.length > 0) {
                setLastShownPlanList(plansWithIds);
              }
            }
          }
        }
      }

      const res = configId
        ? await sendChatMessageWithConfig(messageToSend, idToSend, configId, "GPT")
        : await sendChatMessage(messageToSend, idToSend, "GPT");

      if (isTemp || !convId || convId !== res.conversationId) {
        setCurrentConversationId(res.conversationId);
        onConversationChange?.(res.conversationId);
        if (isTemp) setConversations(prev => prev.filter(c => c.conversationId !== convId));
        loadConversations();
      }

      // Try to extract numbered plan list from bot response for future reference
      if (res.reply && perfCycleState?.id) {
        extractAndSavePlanListFromResponse(res.reply, perfCycleState.id);
      }

      setMessages(prev => [
        ...prev.slice(0, -1),
        { role: "assistant", content: res.reply, timestamp: new Date().toISOString(), model: res.model, suggestions: res.suggestions },
      ]);
    } catch {
      setMessages(prev => [
        ...prev.slice(0, -1),
        { role: "assistant", content: "Sorry, something went wrong. Please try again.", timestamp: new Date().toISOString() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;
    const text = inputValue;
    setInputValue("");
    await sendMessage(text, currentConversationId);
  };

  const handleDeleteConversation = async (convId) => {
    try {
      await deleteConversation(convId);
      loadConversations();
      if (convId === currentConversationId) handleNewConversation();
    } catch (e) { console.error(e); }
  };

  return (
    <div style={{
      display: "flex", height: "100%",
      background: DARK,
      fontFamily: "'Inter', -apple-system, sans-serif",
      overflow: "hidden",
    }}>

      {/* ── Sidebar ── */}
      <div style={{
        width: sidebarOpen ? "270px" : "0px",
        flexShrink: 0,
        background: SIDEBAR_BG,
        borderRight: sidebarOpen ? "1px solid rgba(255,255,255,0.06)" : "none",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
        transition: "width 0.25s ease",
      }}>
        {/* New conversation button */}
        <div style={{ padding: "16px 14px 12px", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
          <button
            onClick={handleNewConversation}
            style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "10px", padding: "10px 14px", cursor: "pointer", color: "#fff",
              fontSize: "13px", fontWeight: 500, transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(241,90,36,0.1)"; e.currentTarget.style.borderColor = BORDER; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}
          >
            <span>New Conversation</span>
            <PlusOutlined style={{ fontSize: "13px", color: ACCENT }} />
          </button>
        </div>

        {/* Date group label */}
        <div style={{ padding: "10px 14px 4px" }}>
          <span style={{ fontSize: "10px", color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "0.08em" }}>Today</span>
        </div>

        {/* Conversation list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 6px 12px" }}>
          {loadingConversations ? (
            <div style={{ textAlign: "center", padding: "24px" }}>
              <Spin size="small" />
            </div>
          ) : conversations.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px", color: TEXT_DIM, fontSize: "12px" }}>
              No conversations yet
            </div>
          ) : (
            conversations.map(conv => (
              <ConvItem
                key={conv.conversationId}
                conv={conv}
                active={conv.conversationId === currentConversationId}
                onSelect={loadConversation}
                onDelete={handleDeleteConversation}
              />
            ))
          )}
        </div>

        {/* Bottom agent info */}
        <div style={{
          padding: "12px 14px",
          borderTop: "1px solid rgba(255,255,255,0.05)",
          display: "flex", alignItems: "center", gap: "10px",
        }}>
          <GemIcon size={32} />
          <div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: "#fff", letterSpacing: "0.03em" }}>
              GEM AGENT
            </div>
            <div style={{ fontSize: "10px", color: TEXT_DIM, marginTop: "1px" }}>
              Optimised for {userRole} Strategy
            </div>
          </div>
        </div>
      </div>

      {/* ── Main area ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>

        {/* Active workspace banner */}
        <div style={{
          padding: "10px 24px",
          background: DARKER,
          borderBottom: "1px solid rgba(255,255,255,0.05)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {/* Sidebar toggle */}
            <button
              onClick={() => setSidebarOpen(v => !v)}
              title={sidebarOpen ? "Hide history" : "Show history"}
              style={{
                width: "28px", height: "28px", borderRadius: "6px",
                background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)",
                color: "#94a3b8", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, marginRight: "4px",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#22c55e" }} />
            <span style={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Active Advisory Workspace
            </span>
            <span style={{ fontSize: "10px", color: TEXT_DIM }}>
              · Direct integration with active company objectives & quarterly forecasts
            </span>
          </div>
          <div style={{
            fontSize: "10px", fontWeight: 700, color: ACCENT,
            border: `1px solid ${ACCENT}`,
            borderRadius: "6px", padding: "3px 10px",
            letterSpacing: "0.05em",
          }}>
            ROLE: {userRole.toUpperCase()}
          </div>
        </div>

        {/* Messages scroll area */}
        <div style={{ flex: 1, overflowY: "auto", padding: "32px 0", scrollbarWidth: "none", msOverflowStyle: "none" }}
          className="gem-messages-scroll">
          <style>{`.gem-messages-scroll::-webkit-scrollbar { display: none; }`}</style>
          <div style={{ maxWidth: "820px", margin: "0 auto", padding: "0 32px" }}>

            {/* Empty state */}
            {messages.length === 0 && !loading && (
              <div style={{ textAlign: "center", paddingTop: "80px" }}>
                <GemIcon size={64} style={{ margin: "0 auto 24px", borderRadius: "18px" }} />
                {/* Live indicator dot */}
                <div style={{ position: "relative", display: "inline-block", marginBottom: "20px" }}>
                  <div style={{
                    position: "absolute", bottom: "-4px", right: "-4px",
                    width: "14px", height: "14px", borderRadius: "50%",
                    background: ACCENT, border: "2px solid " + DARK,
                  }} />
                </div>
                <div style={{ fontSize: "22px", fontWeight: 700, color: "#fff", marginBottom: "10px" }}>
                  Hello, I'm {botName}
                </div>
                <div style={{ fontSize: "14px", color: TEXT_MID, maxWidth: "420px", margin: "0 auto 40px", lineHeight: 1.65 }}>
                  {botSubtitle}. Ask me about your plans, performance cycles, or business strategy.
                </div>

                {/* Suggestion chips */}
                <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                  {SUGGESTIONS_GEM.map((s, i) => (
                    <SuggestionChip
                      key={i}
                      title={s.title}
                      subtitle={s.subtitle}
                      onClick={() => sendMessage(s.title, currentConversationId)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Message list */}
            {messages.map((msg, i) => (
              <MessageBubble
                key={i}
                msg={i === messages.length - 1 ? msg : { ...msg, suggestions: undefined }}
                userImage={userImage}
                onSuggestionClick={(text) => sendMessage(text, currentConversationId)}
              />
            ))}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* ── Input bar ── */}
        <div style={{
          padding: "12px 32px 24px",
          background: DARK,
          flexShrink: 0,
        }}>
          <div style={{ maxWidth: "820px", margin: "0 auto" }}>
            <GemInput
              value={inputValue}
              onChange={setInputValue}
              onSend={handleSend}
              loading={loading}
              placeholder={`Message ${botName}... @ cycles | # plans`}
              configId={configId}
            />
          </div>
        </div>

      </div>
    </div>
  );
}

export default ChatInterfaceWithHistory;
